import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { useNavigate } from 'react-router-dom';
import { 
  collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, 
  serverTimestamp, orderBy, getDoc, setDoc, where 
} from 'firebase/firestore';

const generateRandomCode = () => {
  const prefix = 'BLOG50';
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 3; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return prefix + result;
};

const AdminBlogRewards = () => {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [googleSearchBlogs, setGoogleSearchBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGoogleBlogs, setLoadingGoogleBlogs] = useState(true);
  const [googleSearchStats, setGoogleSearchStats] = useState({
    loading: true,
    claims: 0
  });
  const [newGoogleSearchBlog, setNewGoogleSearchBlog] = useState({
    title: 'Google Search: Credit Cards',
    searchUrl: 'https://www.google.com/search?q=uptodatefinance.com+credit+card',
    active: true
  });
  const [editingGoogleSearchId, setEditingGoogleSearchId] = useState(null);
  const [newBlog, setNewBlog] = useState({
    title: '',
    url: '',
    code: generateRandomCode(),
    active: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentBlogId, setCurrentBlogId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Redirect if not admin or not logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    if (!isAdmin) {
      navigate('/');
      return;
    }
  }, [currentUser, isAdmin, navigate]);

  // Fetch all blog posts
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const blogsRef = collection(db, 'blogRewards');
      const q = query(blogsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const blogsData = [];
      querySnapshot.forEach((doc) => {
        blogsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setBlogs(blogsData);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Google search blogs
  const fetchGoogleSearchBlogs = async () => {
    try {
      setLoadingGoogleBlogs(true);
      const googleBlogsRef = collection(db, 'googleSearchBlogs');
      const q = query(googleBlogsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const blogsData = [];
      querySnapshot.forEach((doc) => {
        blogsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setGoogleSearchBlogs(blogsData);
      
      // Count total claims
      const claimsRef = collection(db, 'users');
      const usersSnapshot = await getDocs(query(claimsRef));
      
      let totalClaims = 0;
      
      // Loop through users and count their Google search claims
      for (const userDoc of usersSnapshot.docs) {
        const userRewardClaims = collection(db, 'users', userDoc.id, 'rewardClaims');
        const userClaimsQuery = query(userRewardClaims);
        const userClaimsSnap = await getDocs(userClaimsQuery);
        
        userClaimsSnap.forEach(doc => {
          const data = doc.data();
          if (data.blogId && (
              data.blogId.includes('.html') || 
              data.blogId.includes('google-search') ||
              data.blogTitle === 'Blog from Google Search'
          )) {
            totalClaims++;
          }
        });
      }
      
      setGoogleSearchStats({
        loading: false,
        claims: totalClaims
      });
    } catch (err) {
      console.error('Error fetching Google search blogs:', err);
      setLoadingGoogleBlogs(false);
      setGoogleSearchStats({loading: false, claims: 0});
    } finally {
      setLoadingGoogleBlogs(false);
    }
  };
  
  // Reset Google search blog form
  const resetGoogleSearchForm = () => {
    setNewGoogleSearchBlog({
      title: 'Google Search: Credit Cards',
      searchUrl: 'https://www.google.com/search?q=uptodatefinance.com+credit+card',
      active: true
    });
    setEditingGoogleSearchId(null);
  };
  
  // Handle Google search blog form input changes
  const handleGoogleSearchChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewGoogleSearchBlog({
      ...newGoogleSearchBlog,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Add/Edit Google search blog
  const handleGoogleSearchSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      
      // Validate form
      if (!newGoogleSearchBlog.title || !newGoogleSearchBlog.searchUrl) {
        setError('Please fill in all fields for Google search blog');
        return;
      }
      
      if (editingGoogleSearchId) {
        // Update existing Google search blog
        const blogRef = doc(db, 'googleSearchBlogs', editingGoogleSearchId);
        await updateDoc(blogRef, {
          title: newGoogleSearchBlog.title,
          searchUrl: newGoogleSearchBlog.searchUrl,
          active: newGoogleSearchBlog.active,
          updatedAt: serverTimestamp()
        });
        
        setSuccess('Google search blog updated successfully');
      } else {
        // Add new Google search blog
        await addDoc(collection(db, 'googleSearchBlogs'), {
          title: newGoogleSearchBlog.title,
          searchUrl: newGoogleSearchBlog.searchUrl,
          active: newGoogleSearchBlog.active,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          claimCount: 0
        });
        
        setSuccess('Google search blog added successfully');
      }
      
      // Reset form and refresh list
      resetGoogleSearchForm();
      fetchGoogleSearchBlogs();
    } catch (err) {
      console.error('Error saving Google search blog:', err);
      setError('Failed to save Google search blog');
    }
  };
  
  // Edit a Google search blog
  const handleEditGoogleSearch = (blog) => {
    setNewGoogleSearchBlog({
      title: blog.title,
      searchUrl: blog.searchUrl,
      active: blog.active || true
    });
    setEditingGoogleSearchId(blog.id);
  };
  
  // Delete a Google search blog
  const handleDeleteGoogleSearch = async (id) => {
    if (window.confirm('Are you sure you want to delete this Google search blog?')) {
      try {
        await deleteDoc(doc(db, 'googleSearchBlogs', id));
        setSuccess('Google search blog deleted successfully');
        fetchGoogleSearchBlogs();
      } catch (err) {
        console.error('Error deleting Google search blog:', err);
        setError('Failed to delete Google search blog');
      }
    }
  };

  // Fetch all data
  useEffect(() => {
    if (currentUser && isAdmin) {
      fetchBlogs();
      fetchGoogleSearchBlogs();
    }
  }, [currentUser, isAdmin]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewBlog({
      ...newBlog,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Generate a new random code
  const handleGenerateCode = () => {
    setNewBlog({
      ...newBlog,
      code: generateRandomCode()
    });
  };

  // Reset the form
  const resetForm = () => {
    setNewBlog({
      title: '',
      url: '',
      code: generateRandomCode(),
      active: true
    });
    setIsEditing(false);
    setCurrentBlogId(null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      
      // Validate form
      if (!newBlog.title || !newBlog.url || !newBlog.code) {
        setError('Please fill in all fields');
        return;
      }
      
      // Check if code is unique
      const duplicateCode = blogs.find(blog => 
        blog.code === newBlog.code && (!isEditing || blog.id !== currentBlogId)
      );
      
      if (duplicateCode) {
        setError('This reward code is already in use');
        return;
      }
      
      if (isEditing && currentBlogId) {
        // Update existing blog
        const blogRef = doc(db, 'blogRewards', currentBlogId);
        await updateDoc(blogRef, {
          title: newBlog.title,
          url: newBlog.url,
          code: newBlog.code,
          active: newBlog.active,
          updatedAt: serverTimestamp()
        });
        
        setSuccess('Blog post updated successfully');
      } else {
        // Add new blog
        await addDoc(collection(db, 'blogRewards'), {
          title: newBlog.title,
          url: newBlog.url,
          code: newBlog.code,
          active: newBlog.active,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          claimCount: 0
        });
        
        setSuccess('Blog post added successfully');
      }
      
      // Reset form and refresh list
      resetForm();
      fetchBlogs();
    } catch (err) {
      console.error('Error saving blog:', err);
      setError('Failed to save blog post');
    }
  };

  // Edit a blog post
  const handleEdit = (blog) => {
    setNewBlog({
      title: blog.title,
      url: blog.url,
      code: blog.code,
      active: blog.active || true
    });
    setIsEditing(true);
    setCurrentBlogId(blog.id);
  };

  // Delete a blog post
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await deleteDoc(doc(db, 'blogRewards', id));
        setSuccess('Blog post deleted successfully');
        fetchBlogs();
      } catch (err) {
        console.error('Error deleting blog:', err);
        setError('Failed to delete blog post');
      }
    }
  };

  // Don't render anything if not admin or still checking
  if (!currentUser || !isAdmin) {
    return null;
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
          Manage Blog Rewards
        </h1>
        
        {/* Success and error messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-600 dark:bg-red-900/30 dark:border-red-800 dark:text-red-400">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md text-green-600 dark:bg-green-900/30 dark:border-green-800 dark:text-green-400">
            {success}
          </div>
        )}
        
        {/* Google Search Configuration */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            Google Search Blog Configuration
          </h2>
          
          {googleSearchStats.loading ? (
            <div className="text-center py-4">
              <svg className="animate-spin h-6 w-6 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading Google search stats...</p>
            </div>
          ) : (
            <div>
              <div className="mb-4">
                <div className="flex items-center mb-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Google Search Claims:</span>
                  <span className="ml-2 text-sm font-bold text-blue-600 dark:text-blue-400">{googleSearchStats.claims}</span>
                </div>
              </div>
              
              <form onSubmit={handleGoogleSearchSubmit} className="mb-8">
                <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">
                  {editingGoogleSearchId ? 'Edit Google Search Blog' : 'Add New Google Search Blog'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={newGoogleSearchBlog.title}
                      onChange={handleGoogleSearchChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Google Search: Credit Cards"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Search URL
                    </label>
                    <input
                      type="url"
                      name="searchUrl"
                      value={newGoogleSearchBlog.searchUrl}
                      onChange={handleGoogleSearchChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="https://www.google.com/search?q=uptodatefinance.com+credit+card"
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="googleActive"
                      name="active"
                      checked={newGoogleSearchBlog.active}
                      onChange={handleGoogleSearchChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded dark:border-gray-600"
                    />
                    <label htmlFor="googleActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Active (visible on earn page)
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  {editingGoogleSearchId && (
                    <button
                      type="button"
                      onClick={resetGoogleSearchForm}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  )}
                  
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-600"
                  >
                    {editingGoogleSearchId ? 'Update Google Search Blog' : 'Add Google Search Blog'}
                  </button>
                </div>
              </form>
              
              {/* Google Search Blog List */}
              <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">
                Google Search Blogs
              </h3>
              
              {loadingGoogleBlogs ? (
                <div className="text-center py-4">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading...</p>
                </div>
              ) : googleSearchBlogs.length === 0 ? (
                <p className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No Google search blogs found. Add your first one above.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                          Title
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                          Search URL
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                          Claims
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                      {googleSearchBlogs.map((blog) => (
                        <tr key={blog.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {blog.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            <a href={blog.searchUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline dark:text-blue-400">
                              {blog.searchUrl.length > 30 ? blog.searchUrl.substring(0, 30) + '...' : blog.searchUrl}
                            </a>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {blog.active !== false ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                Active
                              </span>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {blog.claimCount || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEditGoogleSearch(blog)}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteGoogleSearch(blog.id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Regular Blog form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            {isEditing ? 'Edit Blog Post' : 'Add New Blog Post'}
          </h2>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Blog Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={newBlog.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Read Blog #1 and Earn 50 Coins"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Blog URL
                </label>
                <input
                  type="url"
                  name="url"
                  value={newBlog.url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="https://yourblog.blogspot.com/post-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reward Code
                </label>
                <div className="flex">
                  <input
                    type="text"
                    name="code"
                    value={newBlog.code}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="BLOG50XYZ"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateCode}
                    className="bg-gray-200 text-gray-700 px-3 py-2 rounded-r-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                  >
                    Generate
                  </button>
                </div>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={newBlog.active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded dark:border-gray-600"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Active (visible on earn page)
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              )}
              
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-600"
              >
                {isEditing ? 'Update Blog Post' : 'Add Blog Post'}
              </button>
            </div>
          </form>
        </div>
        
        {/* Blog list */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            Blog Posts
          </h2>
          
          {loading ? (
            <div className="text-center py-4">
              <svg className="animate-spin h-6 w-6 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading blog posts...</p>
            </div>
          ) : blogs.length === 0 ? (
            <p className="text-center py-4 text-gray-500 dark:text-gray-400">
              No blog posts found. Add your first blog post above.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                      URL
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                      Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                      Claims
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                  {blogs.map((blog) => (
                    <tr key={blog.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {blog.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        <a href={blog.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline dark:text-blue-400">
                          {blog.url.length > 30 ? blog.url.substring(0, 30) + '...' : blog.url}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded dark:bg-gray-700">
                          {blog.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {blog.active !== false ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {blog.claimCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(blog)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(blog.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBlogRewards; 